import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getProfileImageWithFallback } from '../../utils/profileImageUtils';
import { updateCheckinStatus, getCheckinProgress, isUserCheckedIn } from '../../utils/checkinUtils';
import QRCode from 'qrcode';
import CheckinGuide from './CheckinGuide';

const CheckinPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tripPlan, setTripPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkinMode, setCheckinMode] = useState('manual'); // 'manual' or 'qr'
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState({
    organizers: [],
    participants: [],
    girls: []
  });

  useEffect(() => {
    if (!tripId) {
      setError('ไม่พบ ID ของทริป');
      setLoading(false);
      return;
    }

    loadTripPlan();
  }, [tripId]);

  const loadTripPlan = async () => {
    try {
      const tripRef = doc(db, 'tripPlans', tripId);
      const tripSnap = await getDoc(tripRef);
      
      if (!tripSnap.exists()) {
        setError('ไม่พบทริปที่ต้องการ');
        setLoading(false);
        return;
      }

      const tripData = tripSnap.data();
      
      // Check if user has permission to access this trip
      const isOwner = tripData.userId === currentUser?.uid;
      const isHelper = tripData.helpers?.some(helper => helper.userId === currentUser?.uid);
      
      if (!isOwner && !isHelper && currentUser?.role !== 'admin') {
        setError('คุณไม่มีสิทธิ์เข้าถึงทริปนี้');
        setLoading(false);
        return;
      }

      setTripPlan({ id: tripSnap.id, ...tripData });
      
      // Load check-in status
      if (tripData.checkinConfig?.status) {
        setCheckinStatus(tripData.checkinConfig.status);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading trip plan:', error);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูลทริป');
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    if (!tripPlan) return;

    setIsGeneratingQR(true);
    try {
      const qrData = {
        tripId: tripPlan.id,
        tripName: tripPlan.tripName,
        timestamp: new Date().toISOString(),
        type: 'checkin'
      };

      const qrCodeDataURL = await QRCode.toDataURL(JSON.stringify(qrData), {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrCodeUrl(qrCodeDataURL);
      setCheckinMode('qr');
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('เกิดข้อผิดพลาดในการสร้าง QR code');
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleManualCheckin = async (groupId, userId) => {
    if (!tripPlan || !currentUser) return;

    try {
      const result = await updateCheckinStatus(tripPlan.id, groupId, userId);
      
      if (result.success) {
        // Update local state
        setCheckinStatus(result.data);
        setTripPlan(prev => ({
          ...prev,
          checkinConfig: {
            ...prev.checkinConfig,
            status: result.data
          }
        }));

        alert('Check-in สำเร็จ!');
      } else {
        alert(result.error === 'User already checked in' ? 'ผู้ใช้คนนี้ได้ check-in แล้ว' : 'เกิดข้อผิดพลาดในการ check-in');
      }
    } catch (error) {
      console.error('Error during manual check-in:', error);
      alert('เกิดข้อผิดพลาดในการ check-in');
    }
  };

  const getGroupMembers = (groupId) => {
    if (!tripPlan) return [];

    switch (groupId) {
      case 'organizers':
        const organizers = [];
        // Add trip owner
        organizers.push({
          id: tripPlan.userId,
          name: tripPlan.userName,
          role: 'owner',
          type: 'organizer'
        });
        // Add helpers
        if (tripPlan.helpers) {
          tripPlan.helpers.forEach(helper => {
            organizers.push({
              id: helper.userId,
              name: helper.email,
              role: 'helper',
              type: 'organizer'
            });
          });
        }
        return organizers;
      
      case 'participants':
        // For now, return empty array as participants are not stored in trip plan
        // This would need to be implemented based on booking data
        return [];
      
      case 'girls':
        // For now, return empty array as girls are not stored in trip plan
        // This would need to be implemented based on booking data
        return [];
      
      default:
        return [];
    }
  };

  const isUserCheckedInLocal = (groupId, userId) => {
    return isUserCheckedIn(tripPlan, groupId, userId);
  };

  const getCheckinProgressLocal = () => {
    return getCheckinProgress(tripPlan);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  if (!tripPlan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่พบทริป</h2>
          <p className="text-gray-600 mb-6">ไม่พบทริปที่ต้องการ</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  const progress = getCheckinProgressLocal();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tripPlan.tripName}</h1>
              <p className="text-gray-600">
                📅 {new Date(tripPlan.tripDate).toLocaleDateString('th-TH')} | 
                📍 {tripPlan.tripLocation}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowGuide(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition text-sm"
              >
                📋 คู่มือ
              </button>
              <button
                onClick={() => navigate('/trip-plans')}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Check-in Progress */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">ความคืบหน้า Check-in</h3>
              <span className="text-sm text-gray-600">
                {progress.checkedIn}/{progress.total} คน
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-1">{progress.percentage}% เสร็จสิ้น</p>
          </div>
        </div>

        {/* Check-in Methods */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">วิธีการ Check-in</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Manual Check-in */}
            {tripPlan.checkinConfig?.settings?.allowManualCheckin && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-2xl">👆</span>
                  <h3 className="font-semibold text-gray-900">Manual Check-in</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  กดปุ่มยืนยัน check-in สำหรับผู้เข้าร่วมแต่ละคน
                </p>
                <button
                  onClick={() => setCheckinMode('manual')}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition ${
                    checkinMode === 'manual'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ใช้ Manual Check-in
                </button>
              </div>
            )}

            {/* QR Code Check-in - Temporarily Hidden */}
            {/* {tripPlan.checkinConfig?.settings?.allowQRCheckin && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-2xl">📱</span>
                  <h3 className="font-semibold text-gray-900">QR Code Check-in</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  สร้าง QR code ให้น้องสแกนเพื่อ check-in
                </p>
                <button
                  onClick={generateQRCode}
                  disabled={isGeneratingQR}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition ${
                    checkinMode === 'qr'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${isGeneratingQR ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isGeneratingQR ? 'กำลังสร้าง...' : 'สร้าง QR Code'}
                </button>
              </div>
            )} */};
          </div>
        </div>

        {/* QR Code Display - Temporarily Hidden */}

        {/* Manual Check-in Interface */}
        {checkinMode === 'manual' && (
          <div className="space-y-6">
            {/* Organizers */}
            {tripPlan.checkinConfig?.enabled?.organizers && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">👨‍💼 ผู้จัด/ผู้ช่วยจัด</h2>
                <div className="space-y-3">
                  {getGroupMembers('organizers').map(member => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{member.name}</div>
                          <div className="text-sm text-gray-600">
                            {member.role === 'owner' ? 'ผู้จัด' : 'ผู้ช่วยจัด'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {isUserCheckedInLocal('organizers', member.id) ? (
                          <span className="text-green-600 font-medium">✅ Check-in แล้ว</span>
                        ) : (
                          <button
                            onClick={() => handleManualCheckin('organizers', member.id)}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition text-sm"
                          >
                            Check-in
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Participants */}
            {tripPlan.checkinConfig?.enabled?.participants && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">👥 ผู้เข้าร่วมกิจกรรม</h2>
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">👥</div>
                  <p className="text-gray-600">ยังไม่มีข้อมูลผู้เข้าร่วมกิจกรรม</p>
                  <p className="text-sm text-gray-500 mt-1">
                    ข้อมูลจะแสดงเมื่อมีการจองคิวกับน้องๆ
                  </p>
                </div>
              </div>
            )}

            {/* Girls */}
            {tripPlan.checkinConfig?.enabled?.girls && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">👧 น้องๆ</h2>
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">👧</div>
                  <p className="text-gray-600">ยังไม่มีข้อมูลน้องๆ</p>
                  <p className="text-sm text-gray-500 mt-1">
                    ข้อมูลจะแสดงเมื่อมีการจองคิวกับน้องๆ
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Check-in Guide Modal */}
      {showGuide && (
        <CheckinGuide onClose={() => setShowGuide(false)} />
      )}
    </div>
  );
};

export default CheckinPage;
